import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  Title,
  Paper,
  Alert,
  Button,
  Stack,
  Loader,
  Center,
} from '@mantine/core';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { authApi } from '@/services/auth.api';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  // Guard against React StrictMode's double-invoke. StrictMode only activates
  // in development builds, so this is a dev-only behaviour, not a production
  // correctness issue. In dev it mounts → unmounts → remounts each component,
  // running the effect twice. Without this guard the second call finds the token
  // already consumed and overwrites 'success' with 'error'. The ref is harmless
  // in production (always stays false after the single invocation).
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    const verifyEmail = async () => {
      try {
        const data = await authApi.verifyEmail(token);
        setStatus('success');
        setMessage(data.message);
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Verification failed');
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <Container size={420} my={40}>
      <Title ta="center" mb="xl">
        Email Verification
      </Title>

      <Paper withBorder shadow="md" p={30} radius="md">
        {status === 'loading' && (
          <Center>
            <Stack align="center">
              <Loader size="lg" />
              <span>Verifying your email...</span>
            </Stack>
          </Center>
        )}

        {status === 'success' && (
          <Stack>
            <Alert icon={<IconCheck size={16} />} title="Success!" color="green">
              {message}
            </Alert>
            <Button fullWidth onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Stack>
        )}

        {status === 'error' && (
          <Stack>
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
              {message}
            </Alert>
            <Button variant="light" fullWidth component={Link} to="/register">
              Back to Registration
            </Button>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}
